import { Activity, ArrowRight, CircleCheck as CheckCircle2, Gauge, RefreshCw, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
interface StepItem {
  number: string;
  title: string;
  description: string;
  icon: typeof Activity;
}
const steps: StepItem[] = [{
  number: "01",
  title: "Baseline",
  description: "Initial calibrated assessment capturing resting metrics, biomechanical markers, and physiological responses.",
  icon: Gauge
}, {
  number: "02",
  title: "Action",
  description: "Implementation of your targeted rehabilitation plan, conditioning block, or movement adjustments.",
  icon: Activity
}, {
  number: "03",
  title: "Reassess",
  description: "Repeated testing under comparable conditions to track shifts in gait, balance, and metabolic profiles over time.",
  icon: RefreshCw
}];
const trackedMetrics = ["Aerobic fitness & efficiency", "Exercise ventilatory response", "Gait symmetry & loading forces", "Dynamic balance & stability", "Metabolic substrate utilisation"];
export default function MeasureChange() {
  return <section id="measurechangeprocess" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border/40">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wider">
            Methodology & Tracking
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight text-foreground">
            Measure change, not just a single result
          </h2>
          <p className="mt-4 text-lg sm:text-xl font-medium text-foreground/90">
            Objective testing becomes particularly useful when repeated under comparable conditions.
          </p>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed">
            Reassessment allows clinicians and clients to evaluate how aerobic fitness, exercise response, gait, balance, and metabolic parameters evolve across weeks and months. Rather than assuming every shift stems directly from a single intervention, longitudinal data offers a transparent picture of your physiological and movement trajectory.
          </p>
        </div>

        {/* Visual Sequence: Baseline -> Action -> Reassess */}
        <div className="mt-12 sm:mt-16">
          <div className="flex items-center gap-2 mb-6 text-sm font-semibold tracking-wide uppercase text-muted-foreground">
            <Sparkles className="size-4 text-primary" />
            <span>Testing Sequence</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
            {steps.map((step, index) => {
            const Icon = step.icon;
            return <div key={step.number} data-index={index} className="relative group">
                  <Card className="h-full border border-border bg-card shadow-xs transition-shadow duration-200 hover:shadow-md">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-mono font-semibold px-2 py-0.5 rounded-sm bg-muted text-muted-foreground">
                          {step.number}
                        </span>
                        <div className="p-2 rounded-lg bg-secondary text-secondary-foreground">
                          <Icon className="size-5 text-primary" />
                        </div>
                      </div>
                      <CardTitle className="text-xl font-semibold mt-3 text-card-foreground">
                        {step.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {step.description}
                      </p>
                    </CardContent>
                  </Card>

                  {/* Flow Arrow indicator between cards on desktop */}
                  {index < steps.length - 1 && <div aria-hidden="true" className="hidden md:flex absolute top-1/2 -right-3 -translate-y-1/2 z-10 size-6 rounded-full bg-background border border-border items-center justify-center shadow-xs text-muted-foreground">
                      <ArrowRight className="size-3.5" />
                    </div>}
                </div>;
          })}
          </div>
        </div>

        {/* Parameters Monitored Panel */}
        <div className="mt-12 rounded-xl border border-border bg-muted/40 p-6 sm:p-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-foreground">
                Key Parameters Tracked Across Reassessments
              </h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                Standardised protocols ensure that variation in testing reflects physiological and mechanical realities rather than measurement inconsistency.
              </p>
            </div>
            <Button asChild size="default" className="shrink-0">
              <Link to="/BookNowPage">Book an Assessment</Link>
            </Button>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-6 border-t border-border">
            {trackedMetrics.map((metric, i) => <div key={metric} data-index={i} className="flex items-center gap-2.5">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span className="text-sm text-foreground/90 font-medium">{metric}</span>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}